<template>
	<Transition name="slide-up">
		<div
			k-data-id="uploadProgress-container"
			v-if="fileProgress"
			:class="[
				'fixed bottom-4 right-4 rounded-lg shadow-lg border p-4 w-80 z-50',
				fileProgress.status === 'error'
					? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
					: 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
			]">
			<div k-data-id="uploadProgress-fileInfo" class="flex items-start justify-between mb-2">
				<div class="flex-1 min-w-0">
					<p
						k-data-id="uploadProgress-fileName"
						:class="[
							'text-sm font-medium truncate',
							fileProgress.status === 'error'
								? 'text-red-900 dark:text-red-100'
								: 'text-gray-900 dark:text-gray-100'
						]">
						{{ fileProgress.file }}
					</p>
					<p
						k-data-id="uploadProgress-statusText"
						:class="[
							'text-xs mt-1',
							fileProgress.status === 'error'
								? 'text-red-700 dark:text-red-300'
								: 'text-gray-500 dark:text-gray-400'
						]">
						{{ statusText }}
					</p>
				</div>
			</div>

			<!-- Progress Bar (only show if not error) -->
			<div
				k-data-id="uploadProgress-progressBarContainer"
				v-if="fileProgress.status !== 'error'"
				class="relative w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
				<div
					k-data-id="uploadProgress-progressBarFill"
					class="absolute top-0 left-0 h-full bg-blue-600 transition-all duration-300 ease-out"
					:style="{ width: `${fileProgress.progress || 0}%` }">
					<!-- Animated shimmer effect -->
					<div
						k-data-id="uploadProgress-shimmer"
						class="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent shimmer"></div>
				</div>
			</div>

			<!-- Percentage Text (only show if not error) -->
			<div
				k-data-id="uploadProgress-progressStats"
				v-if="fileProgress.status !== 'error'"
				class="flex items-center justify-between mt-2">
				<span
					k-data-id="uploadProgress-percentage"
					class="text-xs font-medium text-gray-700 dark:text-gray-300">
					{{ Math.round(fileProgress.progress || 0) }}%
				</span>
				<span k-data-id="uploadProgress-status" class="text-xs text-gray-500 dark:text-gray-400">
					{{ fileProgress.status }}
				</span>
			</div>
		</div>
	</Transition>
</template>

<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'

const store = useStore()

const fileProgress = computed(() => store.state.fileProgress)

const statusText = computed(() => {
	if (!fileProgress.value) return ''

	if (fileProgress.value.status === 'error') {
		return fileProgress.value.message || 'Upload failed'
	}

	const progress = fileProgress.value.progress || 0

	if (progress >= 100) {
		return 'Processing...'
	} else if (progress > 0) {
		return 'Uploading...'
	} else {
		return 'Starting upload...'
	}
})
</script>

<style scoped>
/* Slide up animation */
.slide-up-enter-active,
.slide-up-leave-active {
	transition: all 0.3s ease-out;
}

.slide-up-enter-from {
	transform: translateY(100%);
	opacity: 0;
}

.slide-up-leave-to {
	transform: translateY(100%);
	opacity: 0;
}

/* Shimmer animation for progress bar */
@keyframes shimmer {
	0% {
		transform: translateX(-100%);
	}
	100% {
		transform: translateX(100%);
	}
}

.shimmer {
	animation: shimmer 2s infinite;
}
</style>
